import { snapshotArray } from '../array.js';
import type * as Core from '../../types/calculation-core.js';
import { failure, isFiniteNumber, resolveMetadata, success, validateMetadata, } from './types.js';
export const DEFAULT_SALARY_CONFIG = {
    metadata: {
        calculatorId: 'salary',
        version: '2026.1',
        effectiveFrom: '2026-01-01',
        lastVerifiedAt: '2026-08-18',
        sources: [
            {
                id: 'gib-income-tax-2026',
                title: '2026 gelir vergisi tarifesi',
                publisher: 'Gelir İdaresi Başkanlığı',
                url: 'https://cdn.gib.gov.tr/api/gibportal-file/file/getFileResources?objectKey=arsiv%2Fyardim-kaynaklar%2Fyararli-bilgiler%2Fgelir-vergisi-tarifeleri%2Fgelir-vergisi-tarifesi-2026.pdf',
                verifiedAt: '2026-08-18',
            },
            {
                id: 'sgk-pek-2026',
                title: '2026 prime esas kazanç miktarları',
                publisher: 'Sosyal Güvenlik Kurumu',
                url: 'https://www.sgk.gov.tr/Content/Post/2e0c9e1a-2cfe-4456-af10-49d3de0c58ba/Prime-Esas-Kazanc-Miktarlari-2026-01-14-10-35-39',
                verifiedAt: '2026-08-18',
            },
            {
                id: 'sgk-rates-2026',
                title: 'İşveren prim oranları',
                publisher: 'Sosyal Güvenlik Kurumu',
                url: 'https://www.sgk.gov.tr/Content/Post/c7812ea8-5087-413f-aeb5-d3c1d153e11a/Isveren-Prim-Oranlari-2026-01-13-04-52-38',
                verifiedAt: '2026-08-18',
            },
            {
                id: 'csgb-wage-statistics-2026-07',
                title: 'Temmuz 2026 ücret ve sendikal istatistikleri',
                publisher: 'Çalışma ve Sosyal Güvenlik Bakanlığı',
                url: 'https://www.csgb.gov.tr/yayinlar/calisma-hayati-istatistikleri-e-bulteni/temmuz-2026/ucret-ve-sendikal-istatistikler.html',
                verifiedAt: '2026-08-18',
            },
        ],
        notes: [
            'Standart 4/a çalışanı varsayılır.',
            'Yıllık brüt ve kesintiler 12 aya bölünür; sonuç belirli bir ayın bordrosu değil yıllık ortalama aylık nettir.',
        ],
    },
    taxBrackets: [
        { upTo: 190000, rate: 0.15 },
        { upTo: 400000, rate: 0.20 },
        { upTo: 1500000, rate: 0.27 },
        { upTo: 5300000, rate: 0.35 },
        { upTo: null, rate: 0.40 },
    ],
    grossMinimumWage: 33030,
    employeeSgkRate: 0.14,
    employeeUnemploymentRate: 0.01,
    stampTaxRate: 0.00759,
    sgkCeilingMultiplier: 9,
    maxMonthlyAmount: Number.MAX_SAFE_INTEGER / 12,
    inverse: {
        initialGrossMultiplier: 1.4,
        adjustmentFactor: 1.3,
        tolerance: 0.01,
        maxIterations: 50,
    },
};
function validateRate(value: unknown, field: string, errors: Core.InputError[]) {
    if (typeof value !== 'number' || !isFiniteNumber(value) || value < 0 || value > 1) {
        errors.push({ field, code: 'INVALID_CONFIG', message: 'Oran 0 ile 1 arasında olmalıdır.' });
    }
}
function validateConfig(config: Core.SalaryConfig) {
    const errors = [...validateMetadata(config.metadata)];
    let previous = 0;
    let previousRate = 0;
    const taxBrackets = snapshotArray(config.taxBrackets, (bracket, index, length) => {
        const isLast = index === length - 1;
        if (!bracket || typeof bracket !== 'object') {
            errors.push({ field: `config.taxBrackets[${index}]`, code: 'INVALID_CONFIG', message: 'Vergi dilimi geçersiz.' });
            return;
        }
        validateRate(bracket.rate, `config.taxBrackets[${index}].rate`, errors);
        if (isFiniteNumber(bracket.rate) && bracket.rate < previousRate) {
            errors.push({
                field: `config.taxBrackets[${index}].rate`,
                code: 'INVALID_CONFIG',
                message: 'Vergi dilimi oranları azalmayan sırada olmalıdır.',
            });
        }
        if (isFiniteNumber(bracket.rate))
            previousRate = bracket.rate;
        if (bracket.upTo === null) {
            if (!isLast) {
                errors.push({ field: `config.taxBrackets[${index}].upTo`, code: 'INVALID_CONFIG', message: 'Açık vergi dilimi yalnız son sırada olabilir.' });
            }
        }
        else if (!isFiniteNumber(bracket.upTo) || bracket.upTo <= previous) {
            errors.push({ field: `config.taxBrackets[${index}].upTo`, code: 'INVALID_CONFIG', message: 'Vergi dilimi üst sınırları artan ve pozitif olmalıdır.' });
        }
        else {
            previous = bracket.upTo;
        }
        if (isLast && bracket.upTo !== null) {
            errors.push({ field: `config.taxBrackets[${index}].upTo`, code: 'INVALID_CONFIG', message: 'Son vergi dilimi açık olmalıdır.' });
        }
    });
    if (!taxBrackets || taxBrackets.length === 0) {
        errors.push({ field: 'config.taxBrackets', code: 'INVALID_CONFIG', message: 'En az bir vergi dilimi gereklidir.' });
    }
    for (const [field, value] of [
        ['config.grossMinimumWage', config.grossMinimumWage],
        ['config.sgkCeilingMultiplier', config.sgkCeilingMultiplier],
        ['config.maxMonthlyAmount', config.maxMonthlyAmount],
        ['config.inverse.initialGrossMultiplier', config.inverse?.initialGrossMultiplier],
        ['config.inverse.adjustmentFactor', config.inverse?.adjustmentFactor],
        ['config.inverse.tolerance', config.inverse?.tolerance],
    ] as const) {
        if (!isFiniteNumber(value) || value <= 0) {
            errors.push({ field, code: 'INVALID_CONFIG', message: 'Değer sıfırdan büyük ve sonlu olmalıdır.' });
        }
    }
    if (!Number.isInteger(config.inverse?.maxIterations) || config.inverse.maxIterations <= 0) {
        errors.push({ field: 'config.inverse.maxIterations', code: 'INVALID_CONFIG', message: 'İterasyon sayısı pozitif tam sayı olmalıdır.' });
    }
    validateRate(config.employeeSgkRate, 'config.employeeSgkRate', errors);
    validateRate(config.employeeUnemploymentRate, 'config.employeeUnemploymentRate', errors);
    validateRate(config.stampTaxRate, 'config.stampTaxRate', errors);
    if (isFiniteNumber(config.employeeSgkRate)
        && isFiniteNumber(config.employeeUnemploymentRate)
        && config.employeeSgkRate + config.employeeUnemploymentRate >= 1) {
        errors.push({
            field: 'config.employeeSgkRate',
            code: 'INVALID_CONFIG',
            message: 'Çalışan SGK ve işsizlik oranlarının toplamı 1’den küçük olmalıdır.',
        });
    }
    return { errors, taxBrackets };
}
function calculateProgressiveTax(annualTaxBase: number, brackets: Core.SalaryConfig["taxBrackets"]) {
    let tax = 0;
    let remaining = annualTaxBase;
    let previousUpper = 0;
    for (const bracket of brackets) {
        if (remaining <= 0)
            break;
        const upper = bracket.upTo ?? Number.POSITIVE_INFINITY;
        const amountInBracket = Math.min(upper - previousUpper, remaining);
        tax += amountInBracket * bracket.rate;
        remaining -= amountInBracket;
        previousUpper = upper;
    }
    return tax;
}
function grossToNet(monthlyGross: number, config: Core.SalaryConfig, taxBrackets: Core.SalaryConfig["taxBrackets"]) {
    const annualGross = monthlyGross * 12;
    const monthlySgkBase = Math.min(monthlyGross, config.grossMinimumWage * config.sgkCeilingMultiplier);
    const annualSgkBase = monthlySgkBase * 12;
    const annualEmployeeSgk = annualSgkBase * config.employeeSgkRate;
    const annualEmployeeUnemployment = annualSgkBase * config.employeeUnemploymentRate;
    let annualStampTax = annualGross * config.stampTaxRate;
    const annualIncomeTaxBase = annualGross - annualEmployeeSgk - annualEmployeeUnemployment;
    let annualIncomeTax = calculateProgressiveTax(annualIncomeTaxBase, taxBrackets);
    const annualMinimumGross = config.grossMinimumWage * 12;
    const annualMinimumSgk = annualMinimumGross * config.employeeSgkRate;
    const annualMinimumUnemployment = annualMinimumGross * config.employeeUnemploymentRate;
    const annualMinimumTaxBase = annualMinimumGross - annualMinimumSgk - annualMinimumUnemployment;
    const annualMinimumIncomeTax = calculateProgressiveTax(annualMinimumTaxBase, taxBrackets);
    const annualMinimumStampTax = annualMinimumGross * config.stampTaxRate;
    annualIncomeTax -= Math.min(annualMinimumIncomeTax, annualIncomeTax);
    annualStampTax -= Math.min(annualMinimumStampTax, annualStampTax);
    const annualNet = annualGross
        - annualEmployeeSgk
        - annualEmployeeUnemployment
        - annualIncomeTax
        - annualStampTax;
    return {
        gross: monthlyGross,
        employeeSgk: annualEmployeeSgk / 12,
        employeeUnemployment: annualEmployeeUnemployment / 12,
        incomeTax: annualIncomeTax / 12,
        stampTax: annualStampTax / 12,
        net: annualNet / 12,
    };
}
function allValuesFinite(result: ReturnType<typeof grossToNet>) {
    return Object.values(result).every(Number.isFinite);
}
function allValuesNonNegative(result: ReturnType<typeof grossToNet>) {
    return Object.values(result).every((value) => value >= 0);
}
function netToGross(monthlyNetTarget: number, config: Core.SalaryConfig, taxBrackets: Core.SalaryConfig["taxBrackets"]) {
    let gross = monthlyNetTarget * config.inverse.initialGrossMultiplier;
    let converged = false;
    for (let iteration = 0; iteration < config.inverse.maxIterations; iteration += 1) {
        const result = grossToNet(gross, config, taxBrackets);
        const difference = monthlyNetTarget - result.net;
        if (Math.abs(difference) < config.inverse.tolerance) {
            converged = true;
            break;
        }
        gross += difference * config.inverse.adjustmentFactor;
        if (gross < 0)
            gross = monthlyNetTarget;
    }
    return { result: grossToNet(gross, config, taxBrackets), converged };
}
export function calculateSalary(input: Parameters<typeof Core.calculateSalary>[0], config: Core.SalaryConfig = DEFAULT_SALARY_CONFIG) {
    const metadata = resolveMetadata(config?.metadata, DEFAULT_SALARY_CONFIG.metadata);
    const checked = config && typeof config === 'object'
        ? validateConfig(config)
        : { errors: [{ field: 'config', code: 'INVALID_CONFIG', message: 'Maaş konfigürasyonu geçersiz.' }], taxBrackets: null };
    if (checked.errors.length > 0 || !checked.taxBrackets)
        return failure(checked.errors, metadata);
    const errors = [];
    if (!input || typeof input !== 'object') {
        errors.push({ field: 'input', code: 'REQUIRED', message: 'Hesaplama girdisi gereklidir.' });
    }
    else {
        if (input.mode !== 'gross-to-net' && input.mode !== 'net-to-gross') {
            errors.push({ field: 'mode', code: 'UNSUPPORTED_VALUE', message: 'Maaş hesaplama modu desteklenmiyor.' });
        }
        if (!isFiniteNumber(input.monthlyAmount)) {
            errors.push({ field: 'monthlyAmount', code: 'NOT_FINITE', message: 'Aylık tutar sonlu bir sayı olmalıdır.' });
        }
        else if (input.monthlyAmount <= 0 || input.monthlyAmount > config.maxMonthlyAmount) {
            errors.push({ field: 'monthlyAmount', code: 'OUT_OF_RANGE', message: 'Aylık tutar sıfırdan büyük ve izin verilen sınır içinde olmalıdır.' });
        }
    }
    if (errors.length > 0)
        return failure(errors, metadata);
    if (input.mode === 'gross-to-net') {
        const result = grossToNet(input.monthlyAmount, config, checked.taxBrackets);
        if (!allValuesFinite(result)) {
            return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Maaş sonucu sonlu sayı aralığını aştı.' }], metadata);
        }
        if (!allValuesNonNegative(result)) {
            return failure([{ field: 'result', code: 'INVALID_CONFIG', message: 'Maaş konfigürasyonu negatif sonuç veya kesinti üretti.' }], metadata);
        }
        return success(result, metadata);
    }
    const inverseResult = netToGross(input.monthlyAmount, config, checked.taxBrackets);
    if (!allValuesFinite(inverseResult.result)) {
        return failure([{ field: 'result', code: 'CALCULATION_OVERFLOW', message: 'Maaş sonucu sonlu sayı aralığını aştı.' }], metadata);
    }
    if (!allValuesNonNegative(inverseResult.result)) {
        return failure([{ field: 'result', code: 'INVALID_CONFIG', message: 'Maaş konfigürasyonu negatif sonuç veya kesinti üretti.' }], metadata);
    }
    if (!inverseResult.converged) {
        return failure([{ field: 'monthlyAmount', code: 'NO_CONVERGENCE', message: 'Netten brüte dönüşüm güvenli toleransta yakınsamadı.' }], metadata);
    }
    return success(inverseResult.result, metadata);
}
